// DevHub: view toggle — portaled into ContentHeader's button area on stock
// pages, rendered inline on our custom card pages. Extracted from Root.tsx.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { viewTogglePages } from './navConfig';

// Wait for the Backstage ContentHeader to render before portaling our toggle
// buttons into it.
const PORTAL_RENDER_DELAY_MS = 200;

export function PageViewToggle() {
  const location = useLocation();
  const navigate = useNavigate();
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  const match = viewTogglePages.find(
    p => location.pathname === p.tablePath || location.pathname === p.cardPath,
  );

  // On stock Backstage pages, find the ContentHeader's rightItemsBox to portal into.
  // Must target specifically BackstageContentHeader (not BackstageHeader which is hidden).
  useEffect(() => {
    if (!match) { setPortalTarget(null); return undefined; }
    if (location.pathname !== match.tablePath) { setPortalTarget(null); return undefined; }

    const findTarget = () => {
      // Target the rightItemsBox inside ContentHeader (inside the content article)
      return document.querySelector(
        '[class*="BackstageContent"] [class*="ContentHeader"] [class*="rightItemsBox"]',
      ) || document.querySelector(
        '[class*="BackstageContentHeader-rightItemsBox"]',
      );
    };

    // Wait for DOM to render
    const timer = setTimeout(() => {
      const target = findTarget();
      if (target) setPortalTarget(target);
    }, PORTAL_RENDER_DELAY_MS);

    // Also observe for dynamic rendering
    const observer = new MutationObserver(() => {
      const target = findTarget();
      if (target && target !== portalTarget) setPortalTarget(target);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [match, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!match) return null;

  const isCardView = location.pathname === match.cardPath;

  const toggleButtons = (
    <div className="devhub-page-view-toggle">
      <button
        className={`devhub-page-view-toggle__btn ${!isCardView ? 'is-active' : ''}`}
        type="button"
        onClick={() => navigate(match.tablePath)}
        title="Table view"
      >
        <odx-icon name="core::menu" size="sm" />
      </button>
      <button
        className={`devhub-page-view-toggle__btn ${isCardView ? 'is-active' : ''}`}
        type="button"
        onClick={() => navigate(match.cardPath)}
        title="Card view"
      >
        <odx-icon name="core::tile-menu" size="sm" />
      </button>
    </div>
  );

  // On stock pages: portal into ContentHeader button area
  if (portalTarget && location.pathname === match.tablePath) {
    return createPortal(toggleButtons, portalTarget);
  }

  // Fallback / card pages: render nothing here (card pages have their own toggle)
  return null;
}
